import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Context, Hooks } from "@xt/xtbase";
import { message } from "antd";
import {
  fetchAllUserCoupons,
  formatCouponIsoLocalDateTime,
  post_user_coupons_redeem,
  resolveCouponTenantId,
} from "@/api/coupon-user";
import { normalizeUserCoupon, normalizeRedeemCouponResult, isUsableCouponStatus } from "./normalizeUserCoupon";
import { translateVoucherApiMc } from "./translateVoucherApiMc";
import VoucherCard from "./VoucherCard";
import VoucherClaimModal from "./VoucherClaimModal";
import styles from "./index.module.less";

const { XtContext } = Context;
const { useTranslation } = Hooks;

export default function VoucherContent() {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const userId = appState?.userInfo?.userId;

  const [code, setCode] = useState("");
  const [activeTab, setActiveTab] = useState("usable");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [claimedCoupon, setClaimedCoupon] = useState(null);

  const tenantId = useMemo(() => resolveCouponTenantId(appState), [appState]);

  const loadCoupons = useCallback(async () => {
    if (userId == null || userId === "") {
      setList([]);
      return;
    }
    setLoading(true);
    try {
      /** §3.2：可用 Tab 传 expireAtFrom（当前时刻）；排序按整券截止 */
      const nowIso = formatCouponIsoLocalDateTime(new Date());
      const listExtras =
        activeTab === "usable"
          ? { sortBy: "expireAt", sortOrder: "desc", ...(nowIso ? { expireAtFrom: nowIso } : {}) }
          : { sortBy: "expireAt", sortOrder: "desc" };
      const { ok, items, mc } = await fetchAllUserCoupons(
        { tenantId, userId: String(userId), pageSize: 50, maxPages: 20, ...listExtras },
        {}
      );
      if (!ok) {
        setList([]);
        message.error(translateVoucherApiMc(t, mc, "activity.voucherLoadFailed"));
        return;
      }
      let normalized = (items || []).map(normalizeUserCoupon).filter(Boolean);
      if (activeTab === "usable") {
        normalized = normalized.filter((row) => isUsableCouponStatus(row.status));
      }
      setList(normalized);
    } catch (e) {
      console.error("[voucher] load coupons", e);
      setList([]);
      message.error(translateVoucherApiMc(t, e?.mc, "activity.voucherLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [userId, tenantId, activeTab, t]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const tabs = useMemo(
    () => [
      { key: "all", label: t("activity.voucherTabAll") || "All" },
      { key: "usable", label: t("activity.voucherTabUsable") || "Usable" },
    ],
    [t]
  );

  const handleClaim = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    if (userId == null || userId === "") {
      message.error(t("home.pleaseLogin") || "Please log in first");
      return;
    }
    setSubmitting(true);
    const idempotencyKey =
      typeof globalThis !== "undefined" &&
      globalThis.crypto &&
      typeof globalThis.crypto.randomUUID === "function"
        ? globalThis.crypto.randomUUID()
        : `redeem_${tenantId}_${userId}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    try {
      const res = await post_user_coupons_redeem(
        {
          tenantId,
          userId: Number(userId) || userId,
          redeemKey: trimmed,
          idempotencyKey,
        },
        {}
      );
      if (res && res.rc === 0) {
        setCode("");
        const coupon = normalizeRedeemCouponResult(res.result);
        if (coupon) {
          setClaimedCoupon(coupon);
        } else {
          message.success(t("activity.voucherModalSuccessLine1") || "You have claimed this voucher successfully.");
        }
        loadCoupons();
      }
    } catch (e) {
      console.error("[voucher] redeem", e);
    } finally {
      setSubmitting(false);
    }
  };

  const closeClaimSuccessModal = () => setClaimedCoupon(null);

  return (
    <div className={styles.page}>
      <div className={styles.note}>{t("activity.voucherRedeemNote")}</div>

      <div className={styles.claimBox}>
        <input
          className={styles.codeInput}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleClaim()}
        />
        <button
          type="button"
          className={styles.claimVoucherBtn}
          onClick={handleClaim}
          disabled={submitting || !code.trim()}
        >
          {t("activity.voucherClaimBtn") || "Claim Voucher"}
        </button>
      </div>

      <div className={styles.tabsSection}>
        <div className={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className={styles.list}>
          {loading ? (
            <div className={styles.empty}>{t("activity.rewardClaimLoading") || "Loading..."}</div>
          ) : list.length === 0 ? (
            <div className={styles.empty}>{t("cmpt.noData") || t("weal.coupon.82")}</div>
          ) : (
            list.map((item, idx) => <VoucherCard key={item.id || String(idx)} item={item} t={t} />)
          )}
        </div>
      </div>

      <VoucherClaimModal
        visible={!!claimedCoupon}
        coupon={claimedCoupon}
        onClose={closeClaimSuccessModal}
        titleId="voucher-claim-success-title"
      />
    </div>
  );
}
